import { Link } from "@gromia/components/Link";
import { phone } from "@gromia/components/svg/phone";
import * as s from "@gromia/components/theme/t.module.css";
import { Svg9000ArrowRight } from "@gromia/picons/9000ArrowRight";
import { tuple } from "@gromia/utils";
import cx from "classnames";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import * as React from "react";

const colors = tuple(
  "btnPrimary",
  "btnWhite",
  "btnDanger",
  "btnPrimaryLight",
  "btnOutlineLight1",
  "btnOutlineDark",
  "btnOutlineWhite",
  "btnOutlinePrimary",
  "btnBgTransparent",
);

const size = tuple("btnXl", "btnLg", "btnSm", "btnXs");

export type ButtonColor = typeof colors[number];

type Props = {
  children: NonNullable<React.ReactNode>;
  color: ButtonColor | "btnCta" | "btnCtaMuted";
  size?: typeof size[number];
  leftIconSrc?: React.ReactElement<any>;
  rightIconSrc?: React.ReactElement<any>;
  arrow?: boolean;
  type?: "submit" | "reset" | "button";
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  displayClassName?: string;
  onClick?: () => void;
  triggerRef?: React.Ref<any>;
};

export type ButtonColorProp = Props["color"];

const makeProps = (
  props: Pick<
    Props,
    "color" | "size" | "onClick" | "className" | "displayClassName"
  >,
) => ({
  className: cx(
    s.btn,
    props.color === "btnCta"
      ? cx(s.btnDanger, s.fontSizeBig, s.fontWeightSemiBold)
      : props.color === "btnCtaMuted"
      ? cx(s.btnPrimary)
      : s[props.color as ButtonColor],
    pipe(
      O.fromNullable(props.size),
      O.map(x => s[x]),
      O.getOrElse(() => ""),
    ),
    props.displayClassName ? props.displayClassName : s.dInlineFlex,
    s.alignItemsCenter,
    props.size === "btnXs" ? s.fontSizeSmall1 : null,
    props.className,
  ),
  onClick: props.onClick,
});

function makeChildren(
  props: Omit<Props, "children">,
  value: NonNullable<React.ReactNode>,
) {
  const arrow = pipe(
    O.some(<Svg9000ArrowRight />),
    O.filter(() => props.color === "btnCta" || !!props.arrow),
  );

  const left = O.fromNullable(props.leftIconSrc);
  const right: O.Option<React.ReactNode> = pipe(
    O.fromNullable(props.rightIconSrc),
    O.alt(() => arrow),
  );

  return (
    <React.Fragment>
      {O.toNullable(left)}
      <span
        className={cx(
          pipe(
            left,
            O.fold(
              () => null,
              () => s.ml2,
            ),
          ),
          pipe(
            right,
            O.fold(
              () => null,
              () => s.mr2,
            ),
          ),
        )}
      >
        {value}
      </span>
      {O.toNullable(right)}
    </React.Fragment>
  );
}

export const Button = (props: Props) => (
  <button
    type={props.type || "button"}
    disabled={props.loading || props.disabled}
    ref={props.triggerRef}
    {...makeProps(props)}
  >
    {props.loading
      ? <div className={cx(s.spinnerBorder, s.spinnerBorderSm, s.mr3)} />
      : null}
    {makeChildren(props, props.children)}
  </button>
);

export const LinkButton = (
  props:
    & Props
    & (
      | { to: string; href?: never; target?: string }
      | { to?: never; href: string; target?: string }
    ),
) =>
  props.to
    ? (
      <Link to={props.to} target={props.target} {...makeProps(props)}>
        {makeChildren(props, props.children)}
      </Link>
    )
    : (
      <a href={props.href} target={props.target} {...makeProps(props)}>
        {makeChildren(props, props.children)}
      </a>
    );

export const PhoneButton = (
  props: Omit<Props, "children"> & {
    label: string;
    phoneNumber: string;
  },
) => (
  <a {...makeProps(props)} href={`tel:${props.phoneNumber}`}>
    <span
      className={s.mr2}
      style={{ lineHeight: 0 }}
      dangerouslySetInnerHTML={{ __html: phone }}
    />
    {makeChildren(props, props.label)}
  </a>
);
